import AsyncStorage from '@react-native-async-storage/async-storage';
import { Logger } from './logger';

/**
 * The live notification feed (SignalR / foreground pushes) kept on the device between
 * launches - one copy per signed-in parent.
 *
 * It used to sit under one key for the whole device ('@notifications', '@unread_count')
 * that logout never cleared, so the next parent to sign in on the same phone or browser
 * was shown the previous family's alerts ("Year 12 - Attendance marked", another child's
 * late arrival). Keyed by the parent's user id, a parent only ever loads their own feed,
 * and signing out removes it from the device.
 */

/** The old device-wide keys. They may hold another family's messages, so they are only ever deleted. */
export const LEGACY_FEED_KEYS = ['@notifications', '@unread_count'];

export const feedKey = (ownerId: string) => `@notifications:${ownerId}`;
export const unreadKey = (ownerId: string) => `@unread_count:${ownerId}`;

export interface StoredFeed<T> {
  notifications: T[];
  unreadCount: number;
}

/** This parent's saved feed (empty when there is none). Also drops the old device-wide copy. */
export async function loadFeed<T>(ownerId: string): Promise<StoredFeed<T>> {
  try {
    await AsyncStorage.multiRemove(LEGACY_FEED_KEYS);
  } catch (e) {
    Logger.error('NotificationStore', 'Failed to remove the old shared feed', e);
  }
  try {
    const [stored, unread] = await Promise.all([
      AsyncStorage.getItem(feedKey(ownerId)),
      AsyncStorage.getItem(unreadKey(ownerId)),
    ]);
    const notifications = stored ? JSON.parse(stored) : [];
    const unreadCount = unread ? parseInt(unread, 10) : 0;
    return {
      notifications: Array.isArray(notifications) ? notifications : [],
      unreadCount: Number.isFinite(unreadCount) ? unreadCount : 0,
    };
  } catch (e) {
    Logger.error('NotificationStore', 'Failed to load notifications from storage', e);
    return { notifications: [], unreadCount: 0 };
  }
}

export function saveFeed<T>(ownerId: string, notifications: T[]): void {
  AsyncStorage.setItem(feedKey(ownerId), JSON.stringify(notifications)).catch((e) =>
    Logger.error('NotificationStore', 'Failed to save notifications', e)
  );
}

export function saveUnreadCount(ownerId: string, count: number): void {
  AsyncStorage.setItem(unreadKey(ownerId), count.toString()).catch((e) =>
    Logger.error('NotificationStore', 'Failed to save unread count', e)
  );
}

/** Signing out: this parent's feed leaves the device. */
export async function clearFeed(ownerId: string): Promise<void> {
  try {
    await AsyncStorage.multiRemove([feedKey(ownerId), unreadKey(ownerId), ...LEGACY_FEED_KEYS]);
  } catch (e) {
    Logger.error('NotificationStore', 'Failed to clear notifications', e);
  }
}
